import { useMemo } from 'react'
import { useComposerStore } from '@/stores/composerStore'
import { useCapabilities } from './useCapabilities'
import { useAdapters } from './useAdapters'
import { useFeature } from './useFeature'
import { useSkillCatalog } from './useSkillCatalog'
import { useWorkflows } from './useWorkflows'
import { computeBundleFindings, type Finding } from '@/utils/bundleFindings'

/**
 * P4.T9: returns the current set of deterministic findings for the
 * active bundle (draftBundle ?? serverBundle). Memoized on the bundle
 * reference + catalog data references; loading short-circuit lives
 * inside the memo callback so rules-of-hooks stays honored.
 */
export function useBundleFindings(): Finding[] {
  const draftBundle = useComposerStore((s) => s.draftBundle)
  const serverBundle = useComposerStore((s) => s.serverBundle)
  const activeBundle = draftBundle ?? serverBundle
  // SA-474: the saved speech mode a workflow's web-chat replies ignore.
  const speechMode = useComposerStore((s) => s.persona?.voice_config?.speech_mode ?? null)

  // Catalog routers are feature-gated (tool_framework / workflow_engine);
  // on deployments without them the queries would 404 on every
  // Workshop/Composer open. Disabled queries resolve to the same empty
  // catalogs the swallowed errors produced, so findings are unchanged.
  const toolFrameworkOn = useFeature('tool_framework')
  const workflowEngineOn = useFeature('workflow_engine')

  const capabilitiesQ = useCapabilities({ enabled: toolFrameworkOn })
  const adaptersQ = useAdapters({ enabled: toolFrameworkOn })
  const skillsQ = useSkillCatalog()
  const workflowsQ = useWorkflows({ enabled: workflowEngineOn })

  const anyLoading =
    capabilitiesQ.isLoading ||
    adaptersQ.isLoading ||
    skillsQ.isLoading ||
    workflowsQ.isLoading

  return useMemo(() => {
    if (anyLoading) return []
    return computeBundleFindings(activeBundle, {
      capabilities: capabilitiesQ.data ?? [],
      adapters: adaptersQ.data ?? [],
      skills: skillsQ.data ?? [],
      workflows: workflowsQ.data ?? [],
    }, { workflowEngine: workflowEngineOn, speechMode })
  }, [
    anyLoading,
    activeBundle,
    workflowEngineOn,
    speechMode,
    capabilitiesQ.data, adaptersQ.data, skillsQ.data, workflowsQ.data,
  ])
}
